import {
  deterministicId,
  projectionDefaultsResponseSchema,
  projectionScenarioSchema,
  type ProjectionScenarioRecord,
  type ResolvedProjectionAssumptions,
  type TenantSettings,
} from '@dairy/shared';
import { projectAllScenarios, projectionLinkEligible, resolveAssumptions } from '@dairy/domain';
import { sessionStore } from '@/auth/session';
import { db } from '@/db/db';
import { clientData, saveMany } from '@/db/repo';
import { emptyAssumptions } from './assumption-form';
import { getProjectionDraft, type ProjectionDraft } from './projection-draft';
import type { CachedProjectionDefaults } from './useProjectionDefaults';

export class ProjectionStorageError extends Error {
  constructor(readonly code: 'ownerChanged' | 'changed' | 'missing') {
    super(code);
  }
}
export function projectionOwner() {
  const active = sessionStore.get();
  if (active.status !== 'signedIn' || active.profile?.role !== 'OWNER')
    throw new ProjectionStorageError('ownerChanged');
  return { tenantId: active.profile.tenantId, userId: active.profile.userId };
}
function checkOwner(owner: ReturnType<typeof projectionOwner>) {
  const active = projectionOwner();
  if (active.tenantId !== owner.tenantId || active.userId !== owner.userId)
    throw new ProjectionStorageError('ownerChanged');
}
const recordSignature = (record: ProjectionScenarioRecord) =>
  JSON.stringify(clientData('projectionScenario', { ...record }));

export async function linkProjectionAnimal(record: ProjectionScenarioRecord, animalId: string | null) {
  const owner = projectionOwner();
  const check = () => {
    checkOwner(owner);
    if (record.tenantId && record.tenantId !== owner.tenantId)
      throw new ProjectionStorageError('ownerChanged');
  };
  check();
  return db.transaction('rw', [db.projectionScenarios, db.animals, db.outbox, db.meta], async () => {
    const latest = await db.projectionScenarios.get(record.id);
    if (!latest || latest.deletedAt || recordSignature(latest) !== recordSignature(record))
      throw new ProjectionStorageError('changed');
    if (animalId) {
      const animal = await db.animals.get(animalId);
      if (
        !animal ||
        (animal.tenantId && animal.tenantId !== owner.tenantId) ||
        !projectionLinkEligible(record.input.startDate, animal)
      )
        throw new ProjectionStorageError('changed');
    }
    return (
      await saveMany(
        [{ entity: 'projectionScenario', op: 'upsert', record: { ...latest, linkedAnimalId: animalId } }],
        check,
      )
    )[0]!;
  });
}
export async function saveDraftProjection(draft: ProjectionDraft, name: string, id: string) {
  const owner = projectionOwner();
  const check = () => {
    checkOwner(owner);
    if (draft !== getProjectionDraft()) throw new ProjectionStorageError('changed');
  };
  check();
  const data = projectionScenarioSchema.parse({ name, input: draft.input, assumptions: draft.assumptions });
  // A stable ID makes retries safe; one transaction also prevents replacing an existing scenario.
  return db.transaction('rw', [db.projectionScenarios, db.outbox, db.meta], async () => {
    if (await db.projectionScenarios.get(id)) throw new ProjectionStorageError('changed');
    return (
      await saveMany([{ entity: 'projectionScenario', op: 'upsert', record: { id, ...data } }], check)
    )[0]!;
  });
}
export interface ProjectionReplacement {
  owner: ReturnType<typeof projectionOwner>;
  record: ProjectionScenarioRecord;
  signature: string;
  assumptions: ResolvedProjectionAssumptions;
  settings: TenantSettings;
  context: string;
  results: ReturnType<typeof projectAllScenarios>;
  cached: CachedProjectionDefaults | null;
}
async function assumptionContext(owner: ReturnType<typeof projectionOwner>, settings: TenantSettings) {
  const record = await db.projectionAssumptionSets.get(deterministicId(`proj-assumptions:${owner.tenantId}`));
  const cached = (await db.meta.get('projectionDefaults'))?.value as CachedProjectionDefaults | undefined;
  const parsed = projectionDefaultsResponseSchema.safeParse(cached?.data);
  const usable =
    cached?.tenantId === owner.tenantId && parsed.success && Number.isFinite(Date.parse(cached.fetchedAt))
      ? { ...cached, data: parsed.data }
      : null;
  const entered = record && !record.deletedAt ? record.data : emptyAssumptions();
  return { entered, cached: usable, signature: JSON.stringify({ entered, cached: usable, settings }) };
}
export async function prepareProjectionReplacement(
  record: ProjectionScenarioRecord,
  settings: TenantSettings,
  owner = projectionOwner(),
): Promise<ProjectionReplacement> {
  checkOwner(owner);
  if (record.tenantId && record.tenantId !== owner.tenantId) throw new ProjectionStorageError('ownerChanged');
  const context = await assumptionContext(owner, settings);
  checkOwner(owner);
  const resolution = resolveAssumptions(context.entered, context.cached?.data.values ?? {}, settings);
  if (!resolution.assumptions) throw new ProjectionStorageError('missing');
  return {
    owner,
    record,
    signature: recordSignature(record),
    assumptions: resolution.assumptions,
    settings,
    context: context.signature,
    results: projectAllScenarios(record.input, resolution.assumptions, settings),
    cached: context.cached,
  };
}
export async function replaceProjectionAssumptions(prepared: ProjectionReplacement) {
  const check = () => {
    checkOwner(prepared.owner);
    if (JSON.stringify(sessionStore.get().profile!.settings) !== JSON.stringify(prepared.settings))
      throw new ProjectionStorageError('changed');
  };
  return db.transaction(
    'rw',
    [db.projectionScenarios, db.projectionAssumptionSets, db.outbox, db.meta],
    async () => {
      const latest = await db.projectionScenarios.get(prepared.record.id);
      const context = await assumptionContext(prepared.owner, prepared.settings);
      if (
        !latest ||
        latest.deletedAt ||
        recordSignature(latest) !== prepared.signature ||
        context.signature !== prepared.context
      )
        throw new ProjectionStorageError('changed');
      return (
        await saveMany(
          [
            {
              entity: 'projectionScenario',
              op: 'upsert',
              record: { ...latest, assumptions: prepared.assumptions },
            },
          ],
          check,
        )
      )[0]!;
    },
  );
}
